import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  IHttpPagedResponse,
  IHttpSingleDataResponse,
  IHttpUnpagedResponse,
} from '@irembo-andela/irembogov3-common';
import {
  IServiceTemplate,
  IServiceTemplateDetails,
  ICreateServiceTemplateRequest,
  IUpdateServiceTemplateRequest,
  IServiceTemplateSummary,
  IApplyTemplateToChangeRequestRequest,
  IServiceTemplateStatistics,
  ServiceTemplateStatus,
} from '../models/service-template/service-template.model';
import { FormlyFieldConfig } from '@ngx-formly/core';

@Injectable({
  providedIn: 'root',
})
export class ServiceTemplateService {
  private readonly baseUrl = `${environment.apiGatewayBaseUrl}/service/api/v1/service-templates`;

  constructor(private http: HttpClient) {}

  // Template Management APIs
  createTemplate(
    template: ICreateServiceTemplateRequest
  ): Observable<IHttpSingleDataResponse<IServiceTemplate>> {
    return this.http.post<IHttpSingleDataResponse<IServiceTemplate>>(
      this.baseUrl,
      template
    );
  }

  updateTemplate(
    templateId: string,
    template: IUpdateServiceTemplateRequest
  ): Observable<IHttpSingleDataResponse<IServiceTemplate>> {
    return this.http.put<IHttpSingleDataResponse<IServiceTemplate>>(
      `${this.baseUrl}/${templateId}`,
      template
    );
  }

  getTemplateById(
    templateId: string
  ): Observable<IHttpSingleDataResponse<IServiceTemplate>> {
    return this.http.get<IHttpSingleDataResponse<IServiceTemplate>>(
      `${this.baseUrl}/${templateId}`
    );
  }

  getTemplates(
    queryParams = ''
  ): Observable<IHttpPagedResponse<IServiceTemplateSummary>> {
    return this.http.get<IHttpPagedResponse<IServiceTemplateSummary>>(
      `${this.baseUrl}?${queryParams}`
    );
  }

  deleteTemplate(templateId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${templateId}`);
  }

  publishTemplate(
    templateId: string
  ): Observable<IHttpSingleDataResponse<IServiceTemplate>> {
    return this.http.post<IHttpSingleDataResponse<IServiceTemplate>>(
      `${this.baseUrl}/${templateId}/publish`,
      {}
    );
  }

  unpublishTemplate(
    templateId: string
  ): Observable<IHttpSingleDataResponse<IServiceTemplate>> {
    return this.http.post<IHttpSingleDataResponse<IServiceTemplate>>(
      `${this.baseUrl}/${templateId}/unpublish`,
      {}
    );
  }

  getPublishedTemplates(): Observable<
    IHttpUnpagedResponse<IServiceTemplateSummary>
  > {
    return this.http.get<IHttpUnpagedResponse<IServiceTemplateSummary>>(
      `${this.baseUrl}/published`
    );
  }

  // Template Details APIs - Individual sections (following change request pattern)
  getTemplateFormDefinition(
    templateId: string
  ): Observable<IHttpSingleDataResponse<Record<string, FormlyFieldConfig[]>>> {
    return this.http.get<
      IHttpSingleDataResponse<Record<string, FormlyFieldConfig[]>>
    >(`${this.baseUrl}/${templateId}/form-definition`);
  }

  updateTemplateFormDefinition(
    templateId: string,
    formDefinition: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/form-definition`,
      formDefinition,
      { headers }
    );
  }

  getTemplatePricing(
    templateId: string
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.get<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/pricing`
    );
  }

  updateTemplatePricing(
    templateId: string,
    pricingData: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/pricing`,
      pricingData
    );
  }

  getTemplateWorkflowDefinition(
    templateId: string
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.get<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/workflow-definition`
    );
  }

  updateTemplateWorkflowDefinition(
    templateId: string,
    workflowDefinition: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/workflow-definition`,
      workflowDefinition,
      { headers }
    );
  }

  getTemplateWorkflowNextStepsProcessingTime(
    templateId: string
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.get<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/workflow-next-steps-processing-time`
    );
  }

  updateTemplateWorkflowNextStepsProcessingTime(
    templateId: string,
    workflowData: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/workflow-next-steps-processing-time`,
      workflowData,
      { headers }
    );
  }

  getTemplateTranslation(
    templateId: string
  ): Observable<
    IHttpSingleDataResponse<Record<string, Record<string, unknown>>>
  > {
    return this.http.get<
      IHttpSingleDataResponse<Record<string, Record<string, unknown>>>
    >(`${this.baseUrl}/${templateId}/translation`);
  }

  updateTemplateTranslation(
    templateId: string,
    translations: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/translation`,
      translations,
      { headers }
    );
  }

  getTemplateNextSteps(
    templateId: string
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.get<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/next-steps`
    );
  }

  updateTemplateNextSteps(
    templateId: string,
    nextSteps: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/next-steps`,
      nextSteps,
      { headers }
    );
  }

  getTemplateThirdPartyFormDefinition(
    templateId: string
  ): Observable<
    IHttpSingleDataResponse<Record<string, Record<string, unknown>>>
  > {
    return this.http.get<
      IHttpSingleDataResponse<Record<string, Record<string, unknown>>>
    >(`${this.baseUrl}/${templateId}/third-party-form-definition`);
  }

  updateTemplateThirdPartyFormDefinition(
    templateId: string,
    formDefinition: Record<string, Record<string, unknown>>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/third-party-form-definition`,
      formDefinition,
      { headers }
    );
  }

  getTemplateJourneyMapping(
    templateId: string
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.get<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/journey-mapping`
    );
  }

  updateTemplateJourneyMapping(
    templateId: string,
    journeyData: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${this.baseUrl}/${templateId}/journey-mapping`,
      journeyData
    );
  }

  // Template Usage APIs
  applyTemplateToChangeRequest(
    templateId: string,
    changeRequestId: string
  ): Observable<IHttpSingleDataResponse<any>> {
    return this.http.post<IHttpSingleDataResponse<any>>(
      `${this.baseUrl}/${templateId}/apply-to-change-request/${changeRequestId}`,
      {}
    );
  }

  getTemplateStatistics(): Observable<
    IHttpSingleDataResponse<IServiceTemplateStatistics>
  > {
    return this.http.get<IHttpSingleDataResponse<IServiceTemplateStatistics>>(
      `${this.baseUrl}/statistics`
    );
  }

  // Convert existing service to template
  convertServiceToTemplate(
    changeRequestId: string
  ): Observable<IHttpSingleDataResponse<IServiceTemplate>> {
    return this.http.post<IHttpSingleDataResponse<IServiceTemplate>>(
      `${this.baseUrl}/convert/${changeRequestId}`,
      {}
    );
  }

  // Helper methods for filtering and querying
  buildQueryParams(params: {
    page?: number;
    size?: number;
    status?: ServiceTemplateStatus;
    categoryId?: string;
    search?: string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): string {
    const queryParams = new HttpParams({
      fromObject: {
        ...(params.page !== undefined && { page: params.page.toString() }),
        ...(params.size !== undefined && { size: params.size.toString() }),
        ...(params.status && { status: params.status }),
        ...(params.categoryId && { categoryId: params.categoryId }),
        ...(params.search && { name: params.search }),
        ...(params.sortBy && { sortBy: params.sortBy }),
        ...(params.sortDirection && { sortDirection: params.sortDirection }),
      },
    });
    return queryParams.toString();
  }
}
